import { Input } from './input.js';
import { Camera } from './camera.js';
import { EventBus, EV } from './eventbus.js';

/**
 * 引擎：按这一帧的真实间隔推进 + 每帧渲染（逻辑与画面同拍）。
 * 职责：驱动循环、持有全局服务（事件/输入/相机/数据库/渲染器）、管理场景栈。
 */
export class Engine {
  constructor({ canvas, db, renderer, fixedStep = 1 / 60, maxFrameTime = 1 / 20 }) {
    this.canvas = canvas;
    this.events = new EventBus();
    this.input = new Input(window);
    this.camera = new Camera(canvas.width, canvas.height);
    this.db = db || null;
    this.renderer = renderer || null;
    this.hud = null;

    this.fixedStep = fixedStep;
    this.maxFrameTime = maxFrameTime;
    this._acc = 0;
    this._last = 0;
    this._raf = 0;
    this.running = false;
    this.paused = false;
    this.timeScale = 1;

    this.scenes = new Map();
    this.scene = null;
    this.fps = 60;
    this._fpsAcc = 0;
    this._fpsCount = 0;
    this.elapsed = 0;

    this._loop = this._loop.bind(this);
  }

  registerScene(scene) { this.scenes.set(scene.id, scene); return scene; }

  /** 切换场景（不做过渡动画，后续可加 transition） */
  enterScene(id, opts = {}) {
    const next = this.scenes.get(id);
    if (!next) throw new Error(`场景不存在: ${id}`);
    if (this.scene) {
      this.events.emit('scene:leave', { scene: this.scene });
      this.scene.entities.clear();
    }
    this.scene = next;
    if (!next.loaded && typeof next.build === 'function') {
      next.build(this);
      next.loaded = true;
    }
    this.events.emit('scene:enter', { scene: next, opts });
    return next;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this._last = performance.now();
    this._acc = 0;
    this._raf = requestAnimationFrame(this._loop);
  }

  stop() {
    this.running = false;
    cancelAnimationFrame(this._raf);
  }

  togglePause(v) {
    this.paused = v === undefined ? !this.paused : !!v;
    this.events.emit('game:pause', { paused: this.paused });
  }

  _loop(now) {
    if (!this.running) return;
    let frameTime = (now - this._last) / 1000;
    this._last = now;
    // 时钟倒退、或同一毫秒里来了两个时间戳，别算出个负数往回走
    if (!(frameTime > 0)) frameTime = 0;
    // 掉帧时最多只认这么多时间：宁可这一会儿走得慢点，也不让一步跨过墙
    if (frameTime > this.maxFrameTime) frameTime = this.maxFrameTime;

    // FPS 统计
    this._fpsAcc += frameTime;
    this._fpsCount++;
    if (this._fpsAcc >= 0.5) {
      this.fps = this._fpsCount / this._fpsAcc;
      this._fpsAcc = 0;
      this._fpsCount = 0;
    }

    // 更新与渲染都包一层：某一帧出岔子不能把整个循环带走，
    // 否则画面会整个冻住（时钟不走、角色不动），看着像"卡死了"
    if (!this.paused && frameTime > 0) {
      try {
        // 一帧推一次，步子跟着真实时间走：屏幕 60Hz 也罢 144Hz 也罢，
        // 走起来都是匀速的（早先按固定 1/60 步补帧，画面与步子对不上就会一顿一冲）
        this.update(frameTime * this.timeScale);
      } catch (err) {
        this._onError(err, 'update');
      }
    }

    try {
      this.render();
    } catch (err) {
      this._onError(err, 'render');
    }

    this.input.endFrame();
    this._raf = requestAnimationFrame(this._loop);
  }

  /** 帧内异常：记下来、报一次，然后继续跑下一帧 */
  _onError(err, where) {
    this.lastError = { where, message: String((err && err.message) || err), at: Date.now() };
    this._errCount = (this._errCount || 0) + 1;
    if (this._errCount <= 3) {
      console.error('[洞天居] ' + where + ' 出错：', err);
      if (this.events) {
        this.events.emit(EV.TOAST, { text: '出了点岔子，已跳过这一帧（' + where + '）', kind: 'warn' });
      }
    }
  }

  update(dt) {
    this.elapsed += dt;
    const scene = this.scene;
    if (!scene) return;
    const ctx = {
      engine: this,
      dt,
      input: this.input,
      events: this.events,
      db: this.db,
      scene,
      camera: this.camera,
    };
    for (const sys of scene.systems) {
      if (sys.enabled === false) continue;
      sys.update(dt, ctx);
    }
    this.camera.update(dt);
    scene.gc();
  }

  render() {
    if (!this.renderer || !this.scene) return;
    this.renderer.render(this.scene, this.camera, this);
  }
}
